import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  IonActionSheet,
  IonAlert,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
  useIonRouter,
} from '@ionic/react';
import { chevronDown, ellipsisHorizontal } from 'ionicons/icons';
import { useLocation } from 'react-router-dom';
import { useAppStateChange } from '@/hooks/useAppState';
import { useAppToast } from '@/hooks/useAppToast';
import { MAX_GRP_TXT_RECIPIENTS } from '@/modules/config';
import { ReshareStoryLogic } from '@/modules/share/reshare_story_logic';
import { useSharingInterstitials } from '@/modules/share/useSharingInterstitials';
import { OutboxLogic } from './logic';
import { useOutbox } from './OutboxContext';
import OutboxMenu, { type OutboxMenuAction } from './OutboxMenu';
import OutboxPreviewSheet from './OutboxPreviewSheet';
import OutboxRow, { OutboxRowContent } from './OutboxRow';
import type { OutboxRowStatus, OutboxSend } from './types';
import { useOutboxSender } from './useOutboxSender';
import styles from './OutboxModal.module.css';

/** Room left between the row kept in view and the edge of the list, in px. */
const ROW_REVEAL_MARGIN = 16;

/** What finishing says: the goal crossed, for a news run that crossed it. */
function finishMessage(send: OutboxSend): {
  message: string;
  testId: string;
} {
  const progress = send.runProgress;
  if (
    progress &&
    ReshareStoryLogic.crossedGoal(
      progress.runImpressionCount,
      progress.impressionGoal,
      OutboxLogic.sentPeopleCount(send)
    )
  ) {
    return {
      message: `🏁 YOU got this run over the line — ${ReshareStoryLogic.peoplePhrase(
        progress.impressionGoal ?? 0
      )} reached!`,
      testId: 'news-goal-celebration',
    };
  }
  return { message: 'Messages sent!', testId: 'outbox-finished' };
}

/**
 * The outbox (mocks 9g–9s): every message a share sends, and the one place the
 * sender sends them from.
 *
 * It keeps the sender in one list the whole time. The main button opens the
 * next message in Messages, pre-filled; coming back, the row it opened asks
 * "did it send?", and one tap confirms it and opens the next. Snowball cannot
 * see whether Messages sent anything, so it asks rather than assumes, and the
 * list stays in one order so the sender never loses their place.
 *
 * A tap on a row previews its message; a long-press offers the row's actions:
 * send it again, mark it sent or not, or take it out. The ⋯ menu acts on the
 * whole list. The outbox is a modal of its own rather than a step of the
 * sharing flow, so it can be opened from the Snowball menu at any time and
 * picks up where the sender left off.
 */
const OutboxModal: React.FC = () => {
  const {
    isOpen,
    closeOutbox,
    activeSend: send,
    sends,
    showSend,
    getSend,
    interstitialsShown,
    recordInterstitialShown,
  } = useOutbox();
  const sender = useOutboxSender(send?.id ?? null);
  const router = useIonRouter();
  const location = useLocation();
  const [presentToast] = useAppToast();
  const { beforeSharing, interstitials } = useSharingInterstitials(isOpen, {
    interstitialsShown,
    recordInterstitialShown,
  });

  const [isBusy, setIsBusy] = useState(false);
  const [rowMenu, setRowMenu] = useState<{
    index: number;
    anchor: DOMRect;
  } | null>(null);
  const [overflowAnchor, setOverflowAnchor] = useState<DOMRect | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [settleFirst, setSettleFirst] = useState<{
    awaitingConfirm: number;
    target: number;
  } | null>(null);
  const [isConfirmingClear, setIsConfirmingClear] = useState(false);
  const [isPickingTopic, setIsPickingTopic] = useState(false);

  const run = useCallback(async (work: () => Promise<unknown>) => {
    setIsBusy(true);
    try {
      await work();
    } finally {
      setIsBusy(false);
    }
  }, []);

  const statuses = send ? OutboxLogic.rowStatuses(send) : [];

  // Keep the row the sender is about to act on in view as they confirm their
  // way down a list longer than the screen: the up-next row, or the one
  // waiting on "did it send?" once nothing is left up next. Each confirm
  // changes the list as Messages opens, while the page is going to the
  // background and a scroll may never land, so it is redone when the outbox
  // finishes opening and when the app comes back to the foreground. The
  // scroll element is moved directly, in one step: IonContent's animated
  // scrollByPoint can be left unfinished while the app is backgrounded.
  const contentRef = useRef<HTMLIonContentElement>(null);
  const rowsRef = useRef<HTMLOListElement>(null);
  const focusIndex = statuses.includes('upNext')
    ? statuses.indexOf('upNext')
    : statuses.indexOf('toConfirm');
  const focusId = send && focusIndex !== -1 ? send.items[focusIndex].id : null;
  const revealFocusRow = useCallback(async () => {
    const content = contentRef.current;
    const row =
      rowsRef.current?.querySelector('[data-status="upNext"]') ??
      rowsRef.current?.querySelector('[data-status="toConfirm"]');
    if (!content || !row) return;
    const scroller = await content.getScrollElement();
    const view = scroller.getBoundingClientRect();
    const box = row.getBoundingClientRect();
    const below = box.bottom + ROW_REVEAL_MARGIN - view.bottom;
    const above = box.top - ROW_REVEAL_MARGIN - view.top;
    const delta = below > 0 ? below : above < 0 ? above : 0;
    if (delta !== 0) scroller.scrollTop += delta;
  }, []);
  useEffect(() => {
    if (focusId) revealFocusRow();
  }, [focusId, revealFocusRow]);
  useAppStateChange(isActive => {
    if (isActive && isOpen) revealFocusRow();
  });

  const finish = useCallback(
    (celebrate: boolean) => {
      if (!send) return;
      const finished = getSend(send.id) ?? send;
      closeOutbox();
      const topicPath = `/topics/${encodeURIComponent(send.topicId)}`;
      if (location.pathname !== topicPath) {
        router.push(topicPath, 'forward', 'push');
      }
      if (celebrate) {
        const { message, testId } = finishMessage(finished);
        presentToast({
          message,
          duration: 4000,
          color: 'success',
          htmlAttributes: { 'data-testid': testId },
        });
      }
    },
    [closeOutbox, getSend, location.pathname, presentToast, router, send]
  );

  /**
   * Open a message again, or for the first time out of order. If another
   * message is still waiting on "did it send?", that is settled first (mock
   * 9o).
   */
  const shareAgain = useCallback(
    (index: number) => {
      if (!send) return;
      const awaitingConfirm = OutboxLogic.awaitingConfirmIndex(send);
      if (awaitingConfirm !== -1 && awaitingConfirm !== index) {
        setSettleFirst({ awaitingConfirm, target: index });
        return;
      }
      beforeSharing(() => run(() => sender.openText(index)));
    },
    [beforeSharing, run, send, sender]
  );

  const rowActions = useCallback(
    (index: number, status: OutboxRowStatus): OutboxMenuAction[] => [
      {
        id: 'share-again',
        label:
          status === 'sent' || status === 'toConfirm'
            ? 'Try Sharing Again'
            : 'Share Now',
        onSelect: () => {
          setPreviewIndex(null);
          shareAgain(index);
        },
      },
      ...(status === 'sent'
        ? [
            {
              id: 'mark-not-sent',
              label: 'Mark as Not Sent',
              onSelect: () => run(() => sender.markNotSent(index)),
            },
          ]
        : send && OutboxLogic.canMarkSent(send.items[index])
          ? [
              {
                id: 'mark-sent',
                label: 'Mark as Sent',
                onSelect: () => run(() => sender.markSent(index)),
              },
            ]
          : []),
      {
        id: 'remove',
        label: 'Remove from Outbox',
        destructive: true,
        onSelect: () => {
          setPreviewIndex(null);
          run(() => sender.remove(index));
        },
      },
    ],
    [run, send, sender, shareAgain]
  );

  // Only messages opened in the Messages app can be marked sent, so the ⋯
  // menu offers Mark All as Sent only while one of those is left unsent.
  const overflowActions: OutboxMenuAction[] = [
    ...(send && OutboxLogic.canMarkAllSent(send)
      ? [
          {
            id: 'mark-all-sent',
            label: 'Mark All as Sent',
            onSelect: () => run(sender.markAllSent),
          },
        ]
      : []),
    {
      id: 'mark-all-not-sent',
      label: 'Mark All as Not Sent',
      onSelect: () => run(sender.markAllNotSent),
    },
    {
      id: 'clear',
      label: 'Clear Outbox',
      destructive: true,
      onSelect: () => setIsConfirmingClear(true),
    },
  ];

  const action = send ? OutboxLogic.primaryAction(send) : null;
  const awaitingConfirmItem =
    send && action && 'index' in action && action.kind.startsWith('confirm')
      ? send.items[action.index]
      : null;

  const onPrimary = () => {
    if (!send || !action) return;
    switch (action.kind) {
      case 'start':
      case 'continue':
        beforeSharing(() => run(() => sender.openText(action.index)));
        return;
      case 'confirmNext':
        run(async () => {
          if (await sender.confirm(action.index)) {
            const next = getSend(send.id);
            const nextIndex = next ? OutboxLogic.upNextIndex(next) : -1;
            if (nextIndex !== -1) await sender.openText(nextIndex);
          }
        });
        return;
      case 'confirmFinish':
        run(async () => {
          if (await sender.confirm(action.index)) finish(true);
        });
        return;
      case 'done':
        finish(false);
    }
  };

  const primaryLabel = (() => {
    switch (action?.kind) {
      case 'start':
        return 'Start Sharing';
      case 'continue':
        return 'Share Next';
      case 'confirmNext':
        return 'Confirm & Share Next';
      case 'confirmFinish':
        return 'Confirm & Finish';
      default:
        return 'Done';
    }
  })();

  const others = sends.filter(other => other.id !== send?.id);
  const sentCount = send ? OutboxLogic.sentCount(send) : 0;
  const total = send?.items.length ?? 0;
  const progress = total > 0 ? Math.round((sentCount / total) * 100) : 0;
  const menuRow = send && rowMenu ? (send.items[rowMenu.index] ?? null) : null;
  const previewItem =
    send && previewIndex != null ? (send.items[previewIndex] ?? null) : null;
  const settleAwaiting =
    send && settleFirst ? send.items[settleFirst.awaitingConfirm] : null;
  const settleTarget =
    send && settleFirst ? send.items[settleFirst.target] : null;

  return (
    <IonModal
      isOpen={isOpen}
      onDidPresent={revealFocusRow}
      onDidDismiss={() => {
        setRowMenu(null);
        setOverflowAnchor(null);
        setPreviewIndex(null);
        closeOutbox();
      }}
      className={styles.modal}
      data-testid="outbox"
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={closeOutbox} data-testid="outbox-close">
              Close
            </IonButton>
          </IonButtons>
          <IonTitle>Outbox</IonTitle>
          {send && (
            <IonButtons slot="end">
              <IonButton
                aria-label="More actions"
                data-testid="outbox-more"
                onClick={event =>
                  setOverflowAnchor(
                    (event.currentTarget as HTMLElement).getBoundingClientRect()
                  )
                }
              >
                <IonIcon slot="icon-only" icon={ellipsisHorizontal} />
              </IonButton>
            </IonButtons>
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent
        ref={contentRef}
        style={{ '--background': 'var(--app-page-background)' }}
      >
        <div className={styles.body}>
          {!send ? (
            <p className={styles.empty} data-testid="outbox-empty">
              Your outbox is empty. When you share a topic, its texts wait here
              until you send them.
            </p>
          ) : (
            <>
              <button
                type="button"
                className={styles.topic}
                disabled={others.length === 0}
                onClick={() => setIsPickingTopic(true)}
                data-testid="outbox-topic"
              >
                <span className={styles.topicTitle}>{send.topicTitle}</span>
                {others.length > 0 && (
                  <IonIcon icon={chevronDown} aria-hidden="true" />
                )}
              </button>
              <p className={styles.lede}>
                {OutboxLogic.hasGroupTexts(send)
                  ? `${OutboxLogic.peopleCount(send)} people split into groups of up to ${MAX_GRP_TXT_RECIPIENTS}. Each group is one text you send from your phone.`
                  : 'We step you through one text at a time — your phone sends them, so they arrive as you.'}
              </p>
              <div className={styles.progress}>
                <div className={styles.progressLabels}>
                  <span data-testid="outbox-progress">
                    {sentCount} of {total} sent
                  </span>
                  {OutboxLogic.hasGroupTexts(send) && (
                    <span>{OutboxLogic.peopleCount(send)} people</span>
                  )}
                </div>
                <div
                  className={styles.track}
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={total}
                  aria-valuenow={sentCount}
                >
                  <div
                    className={styles.fill}
                    style={
                      { '--progress': `${progress}%` } as React.CSSProperties
                    }
                  />
                </div>
              </div>
              {send.items.length === 0 ? (
                <p className={styles.empty}>
                  Everyone has been taken out of this send.
                </p>
              ) : (
                <ol
                  ref={rowsRef}
                  className={styles.rows}
                  data-testid="outbox-rows"
                >
                  {send.items.map((item, index) => (
                    <OutboxRow
                      key={item.id}
                      item={item}
                      status={statuses[index]}
                      onTap={() => setPreviewIndex(index)}
                      onLongPress={anchor => setRowMenu({ index, anchor })}
                    />
                  ))}
                </ol>
              )}
            </>
          )}
        </div>
        {menuRow && rowMenu && (
          <OutboxMenu
            anchor={rowMenu.anchor}
            lifted={
              <OutboxRowContent
                item={menuRow}
                status={statuses[rowMenu.index]}
              />
            }
            actions={rowActions(rowMenu.index, statuses[rowMenu.index])}
            onDismiss={() => setRowMenu(null)}
            data-testid="outbox-row-menu"
          />
        )}
        <OutboxMenu
          anchor={overflowAnchor}
          actions={overflowActions}
          align="end"
          onDismiss={() => setOverflowAnchor(null)}
          data-testid="outbox-overflow-menu"
        />
      </IonContent>
      {send && send.items.length > 0 && (
        <IonFooter className={styles.footer}>
          {awaitingConfirmItem && (
            <p className={styles.question} data-testid="outbox-question">
              Did your message to{' '}
              {OutboxLogic.shortName(awaitingConfirmItem.recipients)} get sent?
            </p>
          )}
          <IonButton
            expand="block"
            className={styles.primary}
            disabled={isBusy}
            onClick={onPrimary}
            data-testid="outbox-primary"
          >
            {primaryLabel}
          </IonButton>
          {awaitingConfirmItem && action && 'index' in action && (
            <div className={styles.secondary}>
              <IonButton
                fill="clear"
                size="small"
                disabled={isBusy}
                onClick={() => shareAgain(action.index)}
                data-testid="outbox-try-again"
              >
                Try Sharing Again
              </IonButton>
              <IonButton
                fill="clear"
                size="small"
                color="medium"
                disabled={isBusy}
                onClick={() => run(() => sender.shareLater(action.index))}
                data-testid="outbox-share-later"
              >
                {awaitingConfirmItem.resending
                  ? 'Cancel resend'
                  : 'Share Msg Later'}
              </IonButton>
            </div>
          )}
        </IonFooter>
      )}
      <OutboxPreviewSheet
        item={previewItem}
        status={previewIndex != null ? statuses[previewIndex] : null}
        actions={
          previewIndex != null
            ? rowActions(previewIndex, statuses[previewIndex])
            : []
        }
        onDismiss={() => setPreviewIndex(null)}
      />
      <IonAlert
        isOpen={settleFirst != null}
        header={
          settleAwaiting
            ? `Did your message to ${OutboxLogic.shortName(settleAwaiting.recipients)} get sent?`
            : undefined
        }
        message={
          settleTarget
            ? `Let us know before we share again to ${OutboxLogic.shortName(settleTarget.recipients)}.`
            : undefined
        }
        buttons={[
          {
            text: 'No, Share Later',
            handler: () => {
              const pending = settleFirst;
              if (!pending) return;
              run(async () => {
                await sender.shareLater(pending.awaitingConfirm);
                await sender.openText(pending.target);
              });
            },
          },
          {
            text: 'Yes, Sent',
            handler: () => {
              const pending = settleFirst;
              if (!pending) return;
              run(async () => {
                if (await sender.confirm(pending.awaitingConfirm)) {
                  await sender.openText(pending.target);
                }
              });
            },
          },
        ]}
        backdropDismiss={false}
        onDidDismiss={() => setSettleFirst(null)}
        data-testid="outbox-settle-first"
      />
      <IonAlert
        isOpen={isConfirmingClear}
        header="Clear Outbox?"
        message="This throws out every text in this send, sent or not."
        buttons={[
          { text: 'Cancel', role: 'cancel' },
          {
            text: 'Clear Outbox',
            role: 'destructive',
            handler: () => {
              run(sender.clear);
            },
          },
        ]}
        onDidDismiss={() => setIsConfirmingClear(false)}
        data-testid="outbox-clear-confirm"
      />
      <IonActionSheet
        isOpen={isPickingTopic}
        header="Show the outbox for"
        buttons={[
          ...others.map(other => ({
            text: `${other.topicTitle} · ${OutboxLogic.sentCount(other)} of ${other.items.length} sent`,
            handler: () => showSend(other.id),
          })),
          { text: 'Cancel', role: 'cancel' },
        ]}
        onDidDismiss={() => setIsPickingTopic(false)}
      />
      {interstitials}
    </IonModal>
  );
};

export default OutboxModal;
